export function findTargetA(event: Event): HTMLAnchorElement | null {
  let target = (event.target || event.srcElement) as HTMLElement | null

  while (target && target?.tagName !== 'A') {
    target = target?.parentElement
  }

  return target as HTMLAnchorElement | null
}

export function isNumericKey({ key }: KeyboardEvent) {
  return /\d/.test(key)
}

export function isEnterOrSpaceKey({ key }: KeyboardEvent) {
  return ['Enter', ' '].includes(key)
}

export function useSafariScrollOverflowAnchor<T extends HTMLElement>(
  target: MaybeRefOrGetter<T | null | undefined>,
  options: {
    enabled?: MaybeRefOrGetter<boolean>
    anchorSelector?: string
    interactionToChangeThreshold?: number
  } = {},
) {
  if (!import.meta.client) return
  if (!isSafari(navigator.userAgent)) return

  const {
    enabled: enabledOption = true,
    anchorSelector = '[data-scroll-anchor]',
    interactionToChangeThreshold = 150, // ms
  } = options

  const elRef = computed(() =>
    toValue(enabledOption) ? toValue(target) : undefined,
  )
  const anchors = computed(
    () =>
      [
        ...(elRef.value?.querySelectorAll(anchorSelector).values() || []),
      ] as HTMLElement[],
  )
  const visibleAnchors = reactive(new Set<HTMLElement>())
  const enabled = computed(
    () => toValue(enabledOption) && anchors.value.length > 0,
  )
  const documentRef = computed(() => (enabled.value ? document : undefined))

  function handleIntersectionChange(
    entries: IntersectionObserverEntry[],
  ): void {
    entries.forEach((entry) => {
      const anchor = entry.target as HTMLElement

      if (entry.isIntersecting) {
        visibleAnchors.add(anchor)
      } else {
        visibleAnchors.delete(anchor)
      }
    })
  }

  const centerAnchor = computed(
    () =>
      [...visibleAnchors.values()].sort((a, b) => {
        const position = a.compareDocumentPosition(b)
        return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      })[Math.floor(visibleAnchors.size / 2)],
  )

  watchImmediate(elRef, (el) => {
    if (!el) {
      visibleAnchors.clear()
    }
  })

  const anchorTop = ref(0)
  const hadInteraction = ref(false)

  function handleContentChange() {
    const el = toValue(elRef)
    const anchor = centerAnchor.value

    if (!el || !anchor) return
    if (!hadInteraction.value) return

    const prevTop = anchorTop.value
    const nextTop = anchor.getBoundingClientRect().top

    if (nextTop === prevTop) return

    el.scrollTop += nextTop - prevTop
  }

  function handleUserInteraction(_event: Event) {
    const anchor = centerAnchor.value

    if (!anchor) return

    anchorTop.value = anchor.getBoundingClientRect().top

    hadInteraction.value = true

    // check only these content changes that are related to previous user interaction
    setTimeout(
      () => (hadInteraction.value = false),
      interactionToChangeThreshold,
    )
  }

  useOnTouchClick(documentRef, handleUserInteraction)

  useEventListener(documentRef, 'click', handleUserInteraction, {
    passive: true,
    capture: true,
  })

  useIntersectionObserver(anchors, handleIntersectionChange, {
    root: elRef,
    threshold: [0.5, 1],
    immediate: true,
  })

  const scrollSize = ref([0, 0])

  useMutationObserver(
    elRef,
    () => {
      const el = elRef.value

      if (!el) return

      const [prevWidth, prevHeight] = scrollSize.value

      scrollSize.value = [el.scrollWidth, el.scrollHeight]

      if (
        prevWidth === scrollSize.value[0] &&
        prevHeight === scrollSize.value[1]
      )
        return

      handleContentChange()
    },
    {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    },
  )
}

export function useOnTouchClick<T extends EventTarget>(
  target: MaybeRefOrGetter<T | null | undefined>,
  callback: (event: TouchEvent) => void,
  options: {
    maxTouchClickDuration?: number
    maxTouchDistance?: number
  } = {},
) {
  const { maxTouchClickDuration = 750, maxTouchDistance = 5 } = options

  const touchStartTime = ref(0)
  const touchPosition = ref<readonly [number, number]>()
  const moved = ref(false)

  useEventListener(
    target,
    'touchstart',
    (event: TouchEvent) => {
      moved.value = false
      touchStartTime.value = Date.now()

      if (event.touches.length !== 1) {
        touchPosition.value = undefined
        return
      }

      const [{ clientX, clientY }] = event.touches

      touchPosition.value = [clientX, clientY]
    },
    {
      passive: true,
    },
  )

  useEventListener(
    target,
    'touchmove',
    (event: TouchEvent) => {
      if (!touchPosition.value || moved.value) return

      const [{ clientX, clientY }] = event.touches

      const [prevX, prevY] = touchPosition.value

      const [deltaX, deltaY] = [clientX - prevX, clientY - prevY].map(Math.abs)

      if (deltaX > maxTouchDistance || deltaY > maxTouchDistance) {
        moved.value = true
      }
    },
    {
      passive: true,
    },
  )

  useEventListener(
    target,
    'touchend',
    (event: TouchEvent) => {
      if (!touchPosition.value || moved.value) return
      if (touchStartTime.value + maxTouchClickDuration < Date.now()) return

      callback(event)
    },
    {
      passive: true,
      capture: true,
    },
  )
}
